import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { FileDownload, FileDownloadType } from './fileDownload.model';
import * as actions from './fileDownload.actions';
import { base64, downloadFile } from '@core/util/file';
import { getFileExtensionFromMime } from '@core/util/mime';
import { UploadService } from '@services/file/upload.service';
import { FileNameService } from '@services/file/file-name.service';
import { firstValueFrom } from 'rxjs';
import { DownloadFileStatus } from '@state/utils/models';
import { patch } from '@ngxs/store/operators';
import LogRocket from 'logrocket';
import { CloudConvertApiService } from '../../api-repos/cloud-convert/cloud-convert.api.service';

export interface FileDownloadStateModel {
	downloads: { [id: string]: FileDownload };
}

@UntilDestroy()
@State<FileDownloadStateModel>({
	name: 'fileDownload',
	defaults: {
		downloads: {},
	},
})
@Injectable()
export class FileDownloadState {
	constructor(
		private uploadService: UploadService,
		private fileNameService: FileNameService,
		private cloudConvertApiService: CloudConvertApiService,
	) {}

	@Selector([FileDownloadState])
	static getFileStatus(state: FileDownloadStateModel) {
		return (id: string) => state?.downloads?.[id];
	}

	@Action(actions.DownloadFile)
	async downloadFile(
		{ setState }: StateContext<FileDownloadStateModel>,
		{ payload }: actions.DownloadFile,
	): Promise<void> {
		let { data }: { data: string | base64 | Blob | Uint8Array } = payload;
		const newDownload: FileDownload = {
			id: payload.id,
			status: DownloadFileStatus.DOWNLOADING,
			name: payload.name,
			dataType: payload.dataType,
			data: data,
		};
		try {
			let { type } = payload;
			const { name, byPassFileNaming } = payload;

			const filename = byPassFileNaming
				? name
				: this.fileNameService.getFileName(name ?? 'Document');

			if (payload.mime) {
				const _type = getFileExtensionFromMime(payload.mime);

				type = _type ?? type;
			}

			setState(patch({ downloads: { [payload.id]: newDownload } }));

			if (payload.dataType === FileDownloadType.BASE_64) {
				const _data = payload.data as base64;
				const binary = atob(_data?.data?.replace(/\s/g, ''));
				const len = binary?.length;
				const buffer = new ArrayBuffer(len);
				const view = new Uint8Array(buffer);
				for (let i = 0; i < len; i++) {
					view[i] = binary.charCodeAt(i);
				}
				data = view;
				type = getFileExtensionFromMime(_data.encoding);
			} else if (payload.dataType === FileDownloadType.GOOGLE_URL) {
				const googleResult = await this.uploadService.getBlob(
					payload.data as string,
				);

				type = getFileExtensionFromMime(payload?.type || googleResult?.type);
				data = googleResult;
			} else if (payload.dataType === FileDownloadType.CLOUD_CONVERT_ID) {
				data = await firstValueFrom(
					this.cloudConvertApiService.download(payload.data as string),
				);
			}
			downloadFile(filename, data, type ?? 'pdf');

			setState(
				patch({
					downloads: {
						[payload.id]: {
							...newDownload,
							status: DownloadFileStatus.SUCCESS,
						},
					},
				}),
			);
		} catch (err) {
			LogRocket.log('Unable to download file', err);
			setState(
				patch({
					downloads: {
						[payload.id]: { ...newDownload, status: DownloadFileStatus.FAILED },
					},
				}),
			);
		}
	}
}
